import { useState, useMemo } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import BaseModal from "@components/modals/BaseModal";
import Icon1 from "@assets/icons/Icon";
import type { OrgReception, ReceptionBookingType, ReceptionKind } from "types/org";
import { DEFAULT_RECEPTIONS, getReceptionKind } from "types/org";

const RECEPTION_KIND_LABEL: Record<ReceptionKind, string> = {
  reception: "Réception",
  location: "Location",
  pret: "Véhicule de prêt",
  carrosserie: "Carrosserie",
  divers: "Divers",
};

const COLOR_PRESETS = [
  { color: "#EC4899", bgColor: "#FCE7F3", borderColor: "#FBCFE8", barColor: "#EC4899", label: "Rose" },
  { color: "#DF41FF", bgColor: "#EFC7FF", borderColor: "#E9B9FF", barColor: "#DF41FF", label: "Magenta" },
  { color: "#CC5EFF", bgColor: "#EFD9FF", borderColor: "#E6C0FF", barColor: "#CC5EFF", label: "Violet" },
  { color: "#8B5CF6", bgColor: "#EDE9FE", borderColor: "#DDD6FE", barColor: "#8B5CF6", label: "Mauve" },
  { color: "#8258FF", bgColor: "#DDD2FF", borderColor: "#CDBDFF", barColor: "#A78BFA", label: "Pourpre" },
  { color: "#6F61FF", bgColor: "#D8D4FF", borderColor: "#C5BFFF", barColor: "#9B8FFC", label: "Iris" },
  { color: "#818CF8", bgColor: "#E0E6FF", borderColor: "#CCD0FF", barColor: "#A5B4FC", label: "Indigo" },
  { color: "#5C6BFF", bgColor: "#CACFFF", borderColor: "#B7BDFF", barColor: "#818CF8", label: "Bleu" },
  { color: "#6366F1", bgColor: "#E0E7FF", borderColor: "#C7D2FE", barColor: "#818CF8", label: "Lavande" },
  { color: "#4F46E5", bgColor: "#D5D3FF", borderColor: "#C0BDFF", barColor: "#6366F1", label: "Saphir" },
];

function generateId(): string {
  return `reception_${Date.now().toString(36)}`;
}

function ReceptionModal({
  reception,
  members,
  onClose,
  onSave,
  onDelete,
}: {
  reception: OrgReception | null;
  members: { value: string; label: string }[];
  onClose: () => void;
  onSave: (data: OrgReception) => void;
  onDelete?: () => void;
}) {
  const BOOKING_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: "", label: "Aucun" },
    { value: "reception", label: "Réception (RDV en ligne)" },
    { value: "carrosserie", label: "Carrosserie (RDV en ligne)" },
  ];

  const KIND_OPTIONS: { value: string; label: string }[] = [
    { value: "reception", label: "Réception" },
    { value: "location", label: "Location" },
    { value: "pret", label: "Véhicule de prêt" },
    { value: "carrosserie", label: "Carrosserie" },
    { value: "divers", label: "Divers" },
  ];

  const isCreate = !reception;
  const [label, setLabel] = useState(reception?.label || "");
  const [userId, setUserId] = useState(reception?.user_id || "");
  const [bookingType, setBookingType] = useState<string>(reception?.booking_type || "");
  const [kind, setKind] = useState<ReceptionKind>(reception ? getReceptionKind(reception) : "reception");
  const [presetIdx, setPresetIdx] = useState(() => {
    if (!reception) return 0;
    const idx = COLOR_PRESETS.findIndex((p) => p.color === reception.color);
    return idx >= 0 ? idx : 0;
  });

  const preset = COLOR_PRESETS[presetIdx];

  const memberOptions = useMemo(
    () => [{ value: "", label: "Aucun (non assigné)" }, ...members],
    [members],
  );

  return (
    <BaseModal
      onClose={onClose}
      title={isCreate ? "Nouvelle réception" : "Modifier la réception"}
      startIcon={<Icon1 name="sett-rdv" size={20} />}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <TextField
          label="Nom"
          value={label}
          onChange={(v: string) => setLabel(v)}
          placeholder="ex: Réception 3, Jean-Pierre..."
        />

        <DropDown
          label="Réceptionniste assigné"
          value={userId}
          options={memberOptions}
          onSelect={(v) => setUserId(String(v))}
        />

        <DropDown
          label="Type de réception"
          value={kind}
          options={KIND_OPTIONS}
          onSelect={(v) => setKind(String(v) as ReceptionKind)}
        />

        <DropDown
          label="Type de réservation en ligne"
          value={bookingType}
          options={BOOKING_TYPE_OPTIONS}
          onSelect={(v) => setBookingType(String(v))}
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-h4 text-neutral-400">Couleur</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
            {COLOR_PRESETS.map((p, i) => (
              <button
                key={p.color}
                type="button"
                onClick={() => setPresetIdx(i)}
                className="flex flex-col items-center gap-1"
                style={{
                  padding: "8px 4px",
                  borderRadius: 10,
                  cursor: "pointer",
                  border: presetIdx === i ? "2px solid #333" : "1.5px solid #E5E5E5",
                  background: presetIdx === i ? "#F5F5F5" : "#FAFAFA",
                  transition: "all 0.15s",
                }}
              >
                <div className="rounded-full" style={{ width: 24, height: 24, background: p.color }} />
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 10, color: "#555", textAlign: "center" }}>
                  {p.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-h4 text-neutral-400">Aperçu</span>
          <div
            className="rounded-lg px-3 py-2"
            style={{ background: preset.bgColor, border: `1px solid ${preset.borderColor}` }}
          >
            <span style={{ color: preset.color, fontWeight: 600, fontSize: 13 }}>{label || "Réception"}</span>
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 pt-2">
          <Button
            label={isCreate ? "Créer" : "Enregistrer"}
            onClick={() =>
              onSave({
                id: reception?.id || generateId(),
                label: label.trim() || "Réception",
                color: preset.color,
                bgColor: preset.bgColor,
                borderColor: preset.borderColor,
                barColor: preset.barColor,
                user_id: userId || undefined,
                booking_type: (bookingType as ReceptionBookingType) || undefined,
                kind,
              })
            }
            disabled={!label.trim()}
            variant="small"
          />
          {!isCreate && onDelete && (
            <SecondaryButton label="Supprimer" onClick={onDelete} variant="small" danger />
          )}
        </div>
      </div>
    </BaseModal>
  );
}

export const ReceptionSettings = () => {
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const orgMembers = useDataStore((s) => s.orgMembers);
  const updateOrgData = useDataStore((s) => s.updateOrgData);
  const canEdit = isOwnerOrAdmin(userData);

  const [editTarget, setEditTarget] = useState<OrgReception | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  const receptions: OrgReception[] = orgData?.receptions ?? DEFAULT_RECEPTIONS;

  const memberOptions = useMemo(
    () =>
      orgMembers.map((m) => ({
        value: m.id,
        label: m.infos?.display_name || m.infos?.full_name || m.infos?.email || m.id,
      })),
    [orgMembers],
  );

  const getMemberName = (userId?: string) => {
    if (!userId) return null;
    const m = orgMembers.find((u) => u.id === userId);
    return m ? m.infos?.display_name || m.infos?.full_name || m.infos?.email || userId : userId;
  };

  const saveReceptions = async (newReceptions: OrgReception[]) => {
    if (!orgData?.id) return;
    setSaving(true);
    try {
      await updateOrgData(orgData.id, { receptions: newReceptions });
      showNotification({ message: "Réceptions mises à jour", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la sauvegarde", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = (data: OrgReception) => {
    saveReceptions([...receptions, data]);
    setShowCreate(false);
  };

  const handleUpdate = (data: OrgReception) => {
    saveReceptions(receptions.map((r) => (r.id === data.id ? data : r)));
    setEditTarget(null);
  };

  const handleDelete = () => {
    if (!editTarget) return;
    if (receptions.length <= 1) {
      showNotification({ message: "Il faut au moins une réception", type: "error" });
      return;
    }
    saveReceptions(receptions.filter((r) => r.id !== editTarget.id));
    setEditTarget(null);
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Réceptions</Text1>
          <Text2>Gérez les colonnes de réception de votre planning.</Text2>
          <Text3>
            Ajoutez, renommez ou supprimez des réceptions. Vous pouvez assigner un réceptionniste à chaque colonne.
          </Text3>
        </div>

        <div className="flex flex-col bg-white rounded-lg border-1 overflow-hidden">
          <div className="flex flex-row items-center justify-between px-4 pt-4 pb-2">
            <span className="text-h3 text-neutral-400">
              Réceptions ({receptions.length})
            </span>
            {canEdit && (
              <SecondaryButton
                label="Ajouter"
                variant="small"
                startIcon={<Icon1 name="plus" size={12} />}
                onClick={() => setShowCreate(true)}
              />
            )}
          </div>

          <div className="flex flex-col">
            {receptions.map((rec) => (
              <div
                key={rec.id}
                className="flex flex-row items-center px-4 py-3 border-t-1 hover:bg-neutral-50 transition-colors"
                style={{ gap: 12 }}
              >
                <div
                  className="rounded-full flex-shrink-0"
                  style={{ width: 12, height: 12, background: rec.color }}
                />
                <div
                  className="rounded-lg px-2.5 py-1 flex-shrink-0"
                  style={{ background: rec.bgColor, border: `1px solid ${rec.borderColor}` }}
                >
                  <span style={{ color: rec.color, fontWeight: 600, fontSize: 12 }}>{rec.label}</span>
                </div>
                <span className="text-h3 flex-1 text-neutral-400" style={{ fontFamily: "Poppins" }}>
                  {rec.user_id ? getMemberName(rec.user_id) : "Non assigné"}
                </span>
                <span
                  className="text-h4 rounded-full px-2 py-0.5 flex-shrink-0"
                  style={{ background: "#EEF2FF", color: "#4F46E5" }}
                >
                  {RECEPTION_KIND_LABEL[getReceptionKind(rec)]}
                </span>
                {rec.booking_type && (
                  <span
                    className="text-h4 rounded-full px-2 py-0.5 flex-shrink-0"
                    style={{ background: "#F3F4F6", color: "#6B7280" }}
                  >
                    {rec.booking_type === "carrosserie" ? "Carrosserie" : "Réception"} en ligne
                  </span>
                )}
                {canEdit && (
                  <SecondaryButton
                    label="Gérer"
                    variant="small"
                    onClick={() => setEditTarget(rec)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showCreate && (
        <ReceptionModal
          reception={null}
          members={memberOptions}
          onClose={() => setShowCreate(false)}
          onSave={handleCreate}
        />
      )}
      {editTarget && (
        <ReceptionModal
          reception={editTarget}
          members={memberOptions}
          onClose={() => setEditTarget(null)}
          onSave={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};
